import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  ContentCard,
  Row,
  Tag,
  Tooltip,
} from "@narmi/design_system";
import {
  shouldShowRecentlyChanged,
  type PaginatedDualApproval,
} from "byzantine/src/dbbl/businessLogic/entities/dualApprovals";
import {
  useFetchSwiftAdditionalDetails,
  useFetchSwiftAdditionalFieldsByCountry,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import Address from "../../settings/profile_settings/UserSettingsProfilePage/Address";
import DrawerRows from "./DrawerRow";
import type { DrawerRowProps } from "./DrawerRow";

type RecipientDetailsCardProps = {
  dualApproval: PaginatedDualApproval;
  paymentActivitySnapshot?: API.FormattedPaymentActivity | null;
};

const RecipientDetailsCard = ({
  dualApproval,
  paymentActivitySnapshot,
}: RecipientDetailsCardProps) => {
  const { l10n } = useLocalization();
  const { fetchSwiftAdditionalDetails } = useFetchSwiftAdditionalDetails();
  const { fetchSwiftAdditionalFieldsByCountry } =
    useFetchSwiftAdditionalFieldsByCountry();
  const [institutionDetails, setInstitutionDetails] = useState<{
    [key: string]: string;
  } | null>(null);
  const [renameFields, setRenameFields] = useState<{
    [key: string]: string;
  }>({});

  const isBulk = Boolean(dualApproval.bulkPaymentId);
  const isBulkReversal = Boolean(dualApproval.bulkReversalId);
  const isWire = Boolean(dualApproval.wireId);
  const isInternationalWire = Boolean(dualApproval.internationalWireId);
  const isRecentlyChanged = shouldShowRecentlyChanged({
    createdAt: dualApproval.createdAt,
    recipientUpdatedAt: dualApproval.recipient?.lastUpdatedAt,
    achId: dualApproval.achId,
    fedNowTransactionId: dualApproval.fedNowTransactionId,
    scheduledACHId: dualApproval.scheduledACHId,
  });
  const snapshotDestinationDetails =
    paymentActivitySnapshot?.destinationDetails;
  const recipientName =
    dualApproval.recipientName ||
    snapshotDestinationDetails?.beneficiary_name ||
    "";
  const bankName =
    dualApproval.recipient?.bankName ||
    snapshotDestinationDetails?.institution_name ||
    undefined;
  const accountNumber =
    dualApproval.recipient?.accountNumber ||
    snapshotDestinationDetails?.account_number ||
    undefined;
  const routingNumber =
    dualApproval.recipient?.routingNumber ||
    snapshotDestinationDetails?.routing_number ||
    undefined;

  const recipientBic = dualApproval.recipient?.bic;
  const correspondentBankBic = dualApproval.recipient?.correspondentBankBic;
  const [correspondentBankName, setCorrespondentBankName] = useState<
    string | null
  >(null);

  useEffect(() => {
    const fetchAndSetAdditionalDetails = async (bic: string) => {
      const res = await fetchSwiftAdditionalDetails(bic);
      if (res?.additional_fields) {
        setInstitutionDetails({ institutionName: res.institution_name });
        setRenameFields(res.rename_fields);
      }
    };
    if (recipientBic) {
      setInstitutionDetails(null);
      setRenameFields({});
      fetchAndSetAdditionalDetails(recipientBic);
    }
  }, [recipientBic, fetchSwiftAdditionalDetails]);

  // Separate useEffect for correspondent bank to keep fetches independent,
  // matching the pattern in InternationalWireSection.tsx
  useEffect(() => {
    const fetchCorrespondentBankName = async (bic: string) => {
      const res = await fetchSwiftAdditionalDetails(bic);
      if (res?.institution_name) {
        setCorrespondentBankName(res.institution_name);
      }
      if (dualApproval.recipient?.branchAddress?.country_code) {
        const countryDetails = await fetchSwiftAdditionalFieldsByCountry(
          dualApproval.recipient?.branchAddress?.country_code
        );
        if (countryDetails?.rename_fields) {
          setRenameFields(countryDetails.rename_fields);
        }
      }
    };
    if (!correspondentBankBic) {
      setCorrespondentBankName(null);
      return;
    }

    setCorrespondentBankName(null);
    fetchCorrespondentBankName(correspondentBankBic);
  }, [
    correspondentBankBic,
    fetchSwiftAdditionalDetails,
    fetchSwiftAdditionalFieldsByCountry,
  ]);

  if (isBulk || isBulkReversal) return null;

  const rows: DrawerRowProps[] = [];

  if (isWire) {
    rows.push(
      [l10n.getString("ach-payment-recipient-title"), recipientName],
      [l10n.getString("ach-payment-review-bank"), bankName],
      [l10n.getString("ach-payment-review-account"), accountNumber],
      [l10n.getString("ach-payment-review-routing"), routingNumber],
      [l10n.getString("label-address"), dualApproval.recipient?.address]
    );
  } else if (isInternationalWire) {
    if (dualApproval.recipient?.isOrphanedDestination) {
      rows.push([l10n.getString("ach-payment-recipient-title"), recipientName]);
    } else {
      const isAccountNumber =
        renameFields?.["account_identifier"] === "Account number" ||
        renameFields?.["account_identifier"] === "IBAN";
      rows.push(
        [l10n.getString("ach-payment-recipient-title"), recipientName],
        [
          l10n.getString("label-address"),
          dualApproval.recipient?.addressObj ? (
            <Address
              address={dualApproval.recipient.addressObj as API.Address}
            />
          ) : (
            ""
          ),
        ],
        [
          l10n.getString("ach-payment-review-bank"),
          institutionDetails?.institutionName,
        ],
        [
          l10n.getString("label-international-wire-beneficiary-bank"),
          dualApproval.recipient?.bankName,
        ],
        [l10n.getString("swift-bic"), dualApproval.recipient?.bic],
        [
          l10n.getString("ach-payment-review-account"),
          isAccountNumber ? dualApproval.recipient?.accountIdentifier : "",
        ],
        [
          l10n.getString("label-international-wire-review-correspondent-bank"),
          correspondentBankName ?? undefined,
        ]
      );
    }
  } else {
    rows.push(
      [l10n.getString("ach-payment-recipient-title"), recipientName],
      [l10n.getString("ach-payment-review-bank"), bankName],
      [l10n.getString("ach-payment-review-account"), accountNumber],
      [l10n.getString("ach-payment-review-routing"), routingNumber]
    );
  }

  return (
    <div className="margin--top--xl">
      <Row alignItems="center">
        <Row.Item>
          <h5 className="fontColor--secondary fontWeight--bold margin--bottom--xs lineHeight--body-text">
            {l10n.getString("recipient-details").toUpperCase()}
          </h5>
        </Row.Item>
        {isRecentlyChanged ? (
          <Row.Item shrink>
            <div className="margin--bottom--xs">
              <Tooltip
                maxWidth="210px"
                text={l10n.getString("pending-drawer-recently-changed-copy")}
              >
                <Button kind="plain" testId="recently-changed-tooltip">
                  <Tag
                    kind="error"
                    label={l10n.getString(
                      "approval-drawer-recently-changed-tag"
                    )}
                  />
                </Button>
              </Tooltip>
            </div>
          </Row.Item>
        ) : null}
      </Row>
      <ContentCard kind="bordered">
        {isInternationalWire &&
        dualApproval.recipient?.isOrphanedDestination ? (
          <div className="margin--bottom--m">
            <Alert isActive kind="error" isDismissable={false}>
              {l10n.getString("international-wire-orphaned-recipient-alert")}
            </Alert>
          </div>
        ) : null}
        <DrawerRows rows={rows} />
      </ContentCard>
    </div>
  );
};

export default RecipientDetailsCard;
